import {
  DataProps,
  DeleteOcrResponse,
  FinalizeOcrRequest,
  FinalizeOcrResponse,
  GetOcrResponse,
  UploadOcrResponse,
} from '@diamond/shared/types';
import { apiInstance, buyerCaptureException } from '@diamond/shared/utils';
import { AxiosError, isAxiosError } from 'axios';

import {
  deleteOcrPaymentProofMock,
  finalizeOcrPaymentProofMock,
  getOcrPaymentProofItemsMock,
  hasOcrPaymentProofMockItems,
  uploadOcrPaymentProofMock,
} from './payment-proof-ocr.mock';

export const OCR_PAYMENT_PROOF_QUERY_KEY = ['payment-proof-ocr'] as const;

const url = '/order/payment-proof/ocr';

export const uploadOcrPaymentProof = async (
  token: string,
  orderPaymentId: string,
  files: File[]
): Promise<UploadOcrResponse> => {
  const formData = new FormData();
  formData.append('order_payment_id', orderPaymentId);
  files.forEach((file, index) =>
    formData.append(`file-${index}`, file, file.name)
  );

  try {
    const { data } = await apiInstance(token).post<UploadOcrResponse>(
      url,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return data;
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      const axiosError = error as AxiosError;
      buyerCaptureException(axiosError.response?.data as DataProps, (scope) => {
        scope.setTag(
          'UploadOcrPaymentProof',
          `orderPaymentId ${orderPaymentId}`
        );
        scope.setLevel('error');
      });
    }
    return uploadOcrPaymentProofMock(orderPaymentId, files);
  }
};

export const getOcrPaymentProofItems = async (
  token: string,
  orderPaymentId: string
): Promise<GetOcrResponse> => {
  try {
    const { data } = await apiInstance(token).get<GetOcrResponse>(url, {
      params: { order_payment_id: orderPaymentId },
    });

    if (hasOcrPaymentProofMockItems(orderPaymentId)) {
      return getOcrPaymentProofItemsMock(orderPaymentId);
    }

    return data;
  } catch {
    return getOcrPaymentProofItemsMock(orderPaymentId);
  }
};

export const deleteOcrPaymentProof = async (
  token: string,
  id: string
): Promise<DeleteOcrResponse> => {
  try {
    const { data } = await apiInstance(token).delete<DeleteOcrResponse>(
      `${url}/${id}`
    );
    return data;
  } catch {
    return deleteOcrPaymentProofMock(id);
  }
};

export const finalizeOcrPaymentProof = async (
  token: string,
  paymentId: string,
  body: FinalizeOcrRequest
): Promise<FinalizeOcrResponse> => {
  try {
    const { data } = await apiInstance(token).post<FinalizeOcrResponse>(
      `${url}/finalize`,
      body
    );
    return data;
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      const axiosError = error as AxiosError;
      buyerCaptureException(axiosError.response?.data as DataProps, (scope) => {
        scope.setTag('FinalizeOcrPaymentProof', `paymentId ${paymentId}`);
        scope.setLevel('error');
      });
    }
    return finalizeOcrPaymentProofMock(paymentId, body);
  }
};
